/**
 * Use Restriction Multi-Select Component
 *
 * A reusable multi-select dropdown for use restriction values.
 * Based on the EnumMultiSelect pattern from report-builder.
 * Values are stored as semicolon-separated strings.
 *
 * Free-form values can be added via the "Other..." entry. Any token not in
 * USE_RESTRICTION_OPTIONS (custom or legacy) appears as a checked option so the
 * user can uncheck it to remove it.
 */

import { useState, useRef } from 'react';
import { ChevronDown, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

// Use restriction options
export const USE_RESTRICTION_OPTIONS = [
  { value: 'Defensive', label: 'Defensive' },
  { value: 'Eligibility', label: 'Eligibility' },
  { value: 'Failed Verification - Registrar Handled', label: 'Failed Verification - Registrar Handled' },
  { value: 'Failed Verification - Registry Handled', label: 'Failed Verification - Registry Handled' },
  { value: 'Name Selection', label: 'Name Selection' },
  { value: 'Service Provider', label: 'Service Provider' },
  { value: 'Association', label: 'Association' },
  { value: 'Watch List', label: 'Watch List' },
  { value: 'Compliance', label: 'Compliance' },
  { value: 'Parent/Holding Company', label: 'Parent/Holding Company' },
  { value: 'fTLD Domain', label: 'fTLD Domain' },
  { value: 'Bank In Formation', label: 'Bank In Formation' },
  { value: 'Regulator', label: 'Regulator' },
] as const;

function normalizeUseRestrictionTokens(tokens: string[]): string[] {
  const trimmed = tokens.map((v) => v.trim()).filter(Boolean);

  // De-dupe while preserving order.
  const seen = new Set<string>();
  const unique: string[] = [];
  for (const v of trimmed) {
    if (seen.has(v)) continue;
    seen.add(v);
    unique.push(v);
  }
  return unique;
}

// Helper to parse semicolon-separated string to array
export function parseUseRestriction(value: string | null | undefined): string[] {
  if (!value) return [];
  return normalizeUseRestrictionTokens(value.split(';'));
}

// Helper to convert array to semicolon-separated string
export function formatUseRestriction(values: string[]): string {
  return normalizeUseRestrictionTokens(values).join('; ');
}

interface UseRestrictionSelectProps {
  /** Current value (semicolon-separated string or null) */
  value: string | null | undefined;
  /** Callback when value changes (receives semicolon-separated string) */
  onChange: (value: string) => void;
  /** Placeholder text */
  placeholder?: string;
  /** Additional className for the trigger button */
  className?: string;
  /** Whether the select is disabled */
  disabled?: boolean;
}

export function UseRestrictionSelect({
  value,
  onChange,
  placeholder = 'Select...',
  className,
  disabled = false,
}: UseRestrictionSelectProps) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [isAddingOther, setIsAddingOther] = useState(false);
  const [otherValue, setOtherValue] = useState('');

  // Parse current value to array
  const selectedValues = parseUseRestriction(value);

  const knownOptionsByValue = new Map<string, string>(
    USE_RESTRICTION_OPTIONS.map((o) => [o.value, o.label]),
  );
  const knownValues = new Set<string>(USE_RESTRICTION_OPTIONS.map((o) => o.value));
  const customSelectedValues = selectedValues.filter((v) => !knownValues.has(v));

  // Semicolons are the storage separator, so "A; B" adds two values.
  // Typed values matching a standard option (case-insensitive) use the standard spelling.
  const addOtherValue = () => {
    const entered = parseUseRestriction(otherValue).map((v) =>
      USE_RESTRICTION_OPTIONS.find((o) => o.value.toLowerCase() === v.toLowerCase())?.value ?? v,
    );
    if (entered.length === 0) return;
    onChange(formatUseRestriction([...selectedValues, ...entered]));
    setOtherValue('');
    setIsAddingOther(false);
  };

  // Toggle a value
  const toggleValue = (val: string) => {
    let newValues: string[];
    if (selectedValues.includes(val)) {
      newValues = selectedValues.filter((v) => v !== val);
    } else {
      newValues = [...selectedValues, val];
    }
    onChange(formatUseRestriction(newValues));
  };

  // Get selected labels for display
  const selectedLabels = selectedValues.map((v) => knownOptionsByValue.get(v) ?? v);

  // Filter options by search query, then sort: selected first, then alphabetically
  const filteredOptions = (() => {
    const customOptions = customSelectedValues.map((v) => ({
      value: v,
      label: `${v} (other)`,
    }));

    let options = [...customOptions, ...USE_RESTRICTION_OPTIONS];
    
    // Filter by search query if present
    if (searchQuery) {
      options = options.filter((opt) =>
        opt.label.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    // Sort: selected items first, then alphabetically
    return options.sort((a, b) => {
      const aSelected = selectedValues.includes(a.value);
      const bSelected = selectedValues.includes(b.value);
      
      // Selected items come first
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;
      
      // Then sort alphabetically
      return a.label.localeCompare(b.label);
    });
  })();

  return (
    <Popover
      open={open}
      onOpenChange={(isOpen) => {
        setOpen(isOpen);
        if (!isOpen) {
          setSearchQuery('');
          setIsAddingOther(false);
          setOtherValue('');
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={`h-9 w-full justify-between text-sm font-normal ${className || ''}`}
          disabled={disabled}
        >
          <span className="truncate text-left">
            {selectedLabels.length > 0
              ? selectedLabels.length === 1
                ? selectedLabels[0]
                : `${selectedLabels.length} selected`
              : placeholder}
          </span>
          <ChevronDown className="ml-1 size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="z-[100] w-[280px] p-0"
        align="start"
        sideOffset={4}
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          searchInputRef.current?.focus();
        }}
      >
        <div className="border-b p-2">
          <Input
            ref={searchInputRef}
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-8 text-sm"
          />
        </div>
        <div
          className="pointer-events-auto p-2"
          style={{ maxHeight: '200px', overflowY: 'auto' }}
          onWheel={(e) => e.stopPropagation()}
        >
          {filteredOptions.length === 0 ? (
            <div className="py-2 text-center text-sm text-muted-foreground">
              No results found
            </div>
          ) : (
            <div className="flex flex-col gap-1">
              {filteredOptions.map((opt) => (
                <div
                  key={opt.value}
                  role="button"
                  tabIndex={0}
                  onClick={() => toggleValue(opt.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleValue(opt.value);
                    }
                  }}
                  className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-muted"
                >
                  <Checkbox
                    checked={selectedValues.includes(opt.value)}
                    onCheckedChange={() => toggleValue(opt.value)}
                    className="size-4"
                  />
                  {opt.label}
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="border-t p-2">
          {isAddingOther ? (
            <div className="flex gap-1">
              <Input
                autoFocus
                placeholder="Enter use restriction..."
                value={otherValue}
                onChange={(e) => setOtherValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    e.stopPropagation();
                    addOtherValue();
                  }
                }}
                className="h-8 text-sm"
              />
              <Button
                type="button"
                size="sm"
                className="h-8"
                onClick={addOtherValue}
                disabled={!otherValue.trim()}
              >
                Add
              </Button>
            </div>
          ) : (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 w-full justify-start gap-1 text-xs"
              onClick={() => {
                setOtherValue(searchQuery);
                setIsAddingOther(true);
              }}
            >
              <Plus className="size-3" />
              Other...
            </Button>
          )}
        </div>
        {selectedValues.length > 0 && (
          <div className="border-t p-2">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-full text-xs"
              onClick={() => onChange('')}
            >
              Clear all
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

export default UseRestrictionSelect;
